import { CellValue } from '@app/utils/xlsx';

/**
 * Instructor utilization: how many days each instructor spent delivering.
 *
 * The service answers one row per instructor and month and nothing else - a quarter is three of
 * them added up, a half six and a year twelve - so every period this page shows is derived here.
 * docs/README-utilization.md in class-allocation-service is the authority on how a day gets
 * counted; this file is only the arithmetic on top of the answer.
 */

/** One instructor's delivery in one month, exactly as GET /instructor/utilization sends it. */
export interface InstructorUtilization {
  instructorId: number;
  instructorName: string;
  /** The month the days fall in, as `YYYY-MM`. */
  month: string;
  /**
   * Working days of delivery. **Decimal, not integer** - an RHLS sitting counts half a day, so a
   * month can legitimately read 7.5 and a client reading it as an int reads 1.5 as 1.
   */
  deliveredDays: number;
  /**
   * The days in this month credited with more than a whole day, as `YYYY-MM-DD`. Present and
   * empty rather than absent, and not an error: a full day's class plus a three hour sitting
   * really is a day and a half. 2.0 is usually an instructor recorded on two classes at once.
   */
  overAllocatedDays: string[];
}

// --- The fiscal year -----------------------------------------------------------------------

/**
 * The fiscal year runs April to March and is named by the calendar year it starts in: 2025 is
 * 25/26. Its quarters follow from that - Q1 is Apr-Jun and Q4 is Jan-Mar.
 */
export const FISCAL_YEAR_START_MONTH = 3; // April, zero based

/** The twelve months in the order every table on the page runs in. */
export const FISCAL_MONTH_LABELS = [
  'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'
] as const;

/**
 * The earliest fiscal year the page offers. The service holds classes well before this, but the
 * delivered-days columns the counting leans on were added with changelog 0045 and nothing older
 * has been reviewed, so a year further back would report confidently on data nobody has checked.
 */
export const FIRST_FISCAL_YEAR = 2024;

/** The fiscal year a date falls in, named by the calendar year that year starts in. */
export const fiscalYearOf = (date: Date): number =>
  date.getMonth() >= FISCAL_YEAR_START_MONTH ? date.getFullYear() : date.getFullYear() - 1;

/** `25/26` for 2025. */
export const fiscalYearLabel = (startYear: number): string =>
  `${String(startYear % 100).padStart(2, '0')}/${String((startYear + 1) % 100).padStart(2, '0')}`;

/**
 * The years the picker offers, oldest first: everything from {@link FIRST_FISCAL_YEAR} up to the
 * one we are in. Derived rather than listed, so next April adds its year without a release.
 */
export const fiscalYearOptions = (today: Date = new Date()): number[] => {
  const current = fiscalYearOf(today);
  if (current <= FIRST_FISCAL_YEAR) return [FIRST_FISCAL_YEAR];
  return Array.from({ length: current - FIRST_FISCAL_YEAR + 1 }, (_, index) => FIRST_FISCAL_YEAR + index);
};

/**
 * The twelve `YYYY-MM` keys of a fiscal year, in Apr-Mar order, which is the order the `days`
 * array of a {@link UtilizationSeries} is indexed in.
 */
export const fiscalYearMonths = (startYear: number): string[] =>
  FISCAL_MONTH_LABELS.map((_, index) => {
    const absolute = FISCAL_YEAR_START_MONTH + index;
    const year = startYear + Math.floor(absolute / 12);
    return `${year}-${String((absolute % 12) + 1).padStart(2, '0')}`;
  });

/**
 * The window to ask the endpoint for. Both bounds are inclusive days, which is what `from` and
 * `to` mean there - the page shows months but the service counts dates.
 */
export const fiscalYearRange = (startYear: number): { from: string; to: string } => ({
  from: `${startYear}-04-01`,
  to: `${startYear + 1}-03-31`
});

// --- Periods -------------------------------------------------------------------------------

export type FiscalPeriodKind = 'quarter' | 'half' | 'year';

export interface FiscalPeriod {
  key: string;
  label: string;
  /** Indexes into the twelve months of {@link fiscalYearMonths}, April being 0. */
  months: number[];
  kind: FiscalPeriodKind;
}

const monthRange = (first: number, count: number): number[] =>
  Array.from({ length: count }, (_, index) => first + index);

/** The months a period runs from and to, as labels: `Apr - Jun`, `Apr - Sep`, `Apr - Mar`. */
export const periodMonthSpan = (period: Pick<FiscalPeriod, 'months'>): string => {
  const first = period.months[0] ?? 0;
  const last = period.months[period.months.length - 1] ?? 0;
  return `${FISCAL_MONTH_LABELS[first]} - ${FISCAL_MONTH_LABELS[last]}`;
};

const quarter = (key: string, first: number): FiscalPeriod =>
  ({ key, label: key, months: monthRange(first, 3), kind: 'quarter' });

/** A tuple, so {@link FISCAL_PERIOD_ROWS} can name a quarter by position without a check. */
export const FISCAL_QUARTERS: readonly [FiscalPeriod, FiscalPeriod, FiscalPeriod, FiscalPeriod] = [
  quarter('Q1', 0), quarter('Q2', 3), quarter('Q3', 6), quarter('Q4', 9)
];

export const FISCAL_H1: FiscalPeriod =
  { key: 'H1', label: 'H1', months: monthRange(0, 6), kind: 'half' };

export const FISCAL_H2: FiscalPeriod =
  { key: 'H2', label: 'H2', months: monthRange(6, 6), kind: 'half' };

export const FISCAL_YEAR_PERIOD: FiscalPeriod =
  { key: 'YEAR', label: 'Year', months: monthRange(0, 12), kind: 'year' };

/**
 * Every period the table shows, in reading order: each half falls directly after the two quarters
 * it sums, and the year after the lot. The header and every row walk this one list, which is what
 * stops a column drifting out of step with the figure under it.
 */
export const FISCAL_PERIOD_ROWS: FiscalPeriod[] = [
  FISCAL_QUARTERS[0], FISCAL_QUARTERS[1], FISCAL_H1,
  FISCAL_QUARTERS[2], FISCAL_QUARTERS[3], FISCAL_H2,
  FISCAL_YEAR_PERIOD
];

// --- A subject's year ------------------------------------------------------------------------

/** One instructor's twelve months of a fiscal year, or the whole selection's. */
export interface UtilizationSeries {
  /** The instructor, or null for a row that combines several of them. */
  instructorId: number | null;
  instructorName: string;
  /** Delivered days per month, Apr-Mar. A month the service did not report reads 0 - see below. */
  days: number[];
  /** Over-allocated dates per month, Apr-Mar, gathered from every instructor the row covers. */
  overAllocated: string[][];
}

const emptyMonths = (): number[] => new Array(12).fill(0);
const emptyDates = (): string[][] => Array.from({ length: 12 }, () => [] as string[]);

/**
 * The answer, turned into one series per instructor, sorted by name.
 *
 * **A month with no delivery becomes a zero.** The service omits it rather than sending a zero,
 * deliberately, because only the caller can say whether the silence means "available and taught
 * nothing" or "not yet hired". This page answers that the same way its averages do: every month
 * of the fiscal year counts, so an empty one is a zero that pulls the average down. See
 * {@link periodFigures}.
 *
 * `include` names instructors that must get a row whether or not they delivered anything - the
 * ones the filter resolved to. Without it a search for three instructors, two of whom were idle,
 * would answer with one row and look like a filter that had not been applied.
 */
export const toUtilizationSeries = (
  rows: InstructorUtilization[],
  startYear: number,
  include: { id: number; name: string }[] = []
): UtilizationSeries[] => {
  const monthIndex = new Map(fiscalYearMonths(startYear).map((key, index) => [key, index]));
  const byInstructor = new Map<number, UtilizationSeries>();

  const seed = (id: number, name: string): UtilizationSeries => {
    const existing = byInstructor.get(id);
    if (existing) return existing;
    const created: UtilizationSeries = {
      instructorId: id, instructorName: name, days: emptyMonths(), overAllocated: emptyDates()
    };
    byInstructor.set(id, created);
    return created;
  };

  // The requested instructors first, so every one of them has a row; the rows then overwrite the
  // name with the one the service carries, which is the current one.
  include.forEach(instructor => seed(instructor.id, instructor.name));

  rows.forEach(row => {
    const index = monthIndex.get(row.month);
    // A month outside the year asked for. The page's own request asks for exactly these twelve,
    // but a widened range must not have its tail land in April.
    if (index === undefined) return;

    const series = seed(row.instructorId, row.instructorName);
    if (row.instructorName) series.instructorName = row.instructorName;

    // Added rather than assigned: the contract is one row per instructor and month, and adding
    // is what makes a duplicate read as two deliveries instead of silently dropping one.
    series.days[index] = (series.days[index] ?? 0) + Number(row.deliveredDays ?? 0);
    series.overAllocated[index] = [...(series.overAllocated[index] ?? []), ...(row.overAllocatedDays ?? [])];
  });

  return [...byInstructor.values()].sort((a, b) => a.instructorName.localeCompare(b.instructorName));
};

/**
 * Whether this subject delivered anything at all in the year.
 *
 * What the page's "hide instructors with no delivery" switch filters on. A whole year of zeros is
 * not necessarily an idle instructor - it is equally one who joined after the year ended or left
 * before it began, and the service cannot tell those apart (see {@link toUtilizationSeries}) - so
 * the switch is offered rather than applied, and the rows are counted either way.
 */
export const hasDelivery = (series: UtilizationSeries): boolean =>
  series.days.some(days => days > 0);

/**
 * Every series added together, month by month.
 *
 * **The sum across instructors is deliberately larger than the days delivered**, because two
 * instructors on one class both spent those days in that room and the service credits each of
 * them in full.
 */
export const combineSeries = (series: UtilizationSeries[], label: string): UtilizationSeries => {
  const days = emptyMonths();
  const overAllocated = emptyDates();

  series.forEach(one => {
    one.days.forEach((value, index) => { days[index] = (days[index] ?? 0) + value; });
    one.overAllocated.forEach((dates, index) => {
      overAllocated[index] = [...(overAllocated[index] ?? []), ...dates];
    });
  });

  return { instructorId: null, instructorName: label, days, overAllocated };
};

/**
 * A total divided by the instructors who delivered any of it.
 *
 * **The denominator deliberately leaves out the instructors who delivered nothing**, and is taken
 * from the whole selection rather than from whatever is on screen. Both halves of that matter:
 *
 * - Counting a year of zeros in the denominator would answer a different question. A team of three
 *   where one person delivered 60 days averages 60, not 20 - the other two were not half-booked,
 *   they were not booked. (And a year of zeros is as likely to be somebody who had not joined yet;
 *   see {@link hasDelivery}.)
 * - Reading it off the visible rows would make the figure move when the "hide instructors with no
 *   delivery" switch is thrown, and an average that changes because somebody hid a row of zeros is
 *   an average nobody can quote.
 *
 * Both are why this takes the count as an argument rather than deriving it from the row it is
 * dividing: the caller is the only one that knows which set it means.
 *
 * Monthly values come out fractional, and the period figures derived from them follow - a quarter's
 * total on this row is the quarter's total per instructor, and its average that figure over three
 * months. Rounding is left to the display, so the columns still add up.
 */
export const perInstructorAverage = (
  total: UtilizationSeries,
  deliveringCount: number,
  label: string
): UtilizationSeries => ({
  instructorId: null,
  instructorName: label,
  days: total.days.map(days => (deliveringCount > 0 ? days / deliveringCount : 0)),
  // An over-allocated day belongs to a person and a date. An average over people has neither.
  overAllocated: emptyDates()
});

// --- Figures -------------------------------------------------------------------------------

export interface PeriodFigures {
  total: number;
  average: number;
}

/**
 * What a period totals, and its average per month.
 *
 * **The denominator is the months the period has, not the months that reported a figure**: a
 * quarter divides by 3, a half by 6 and the year by 12, whatever was delivered in them. So a
 * quarter with one quiet month averages lower than a quarter with two busy ones, which is the
 * reading that was asked for - the alternative, dividing by the months that happen to carry a
 * figure, makes an instructor who delivered in a single month of the quarter look fully booked.
 */
export const periodFigures = (days: number[], period: FiscalPeriod): PeriodFigures => {
  const total = period.months.reduce((sum, index) => sum + (days[index] ?? 0), 0);
  return { total, average: total / period.months.length };
};

/** Every over-allocated date the period holds, oldest first and without repeats. */
export const periodOverAllocated = (overAllocated: string[][], period: FiscalPeriod): string[] =>
  [...new Set(period.months.flatMap(index => overAllocated[index] ?? []))].sort();

/**
 * One decimal place, and none at all on a whole number: `10`, `10.5`, `8.3`.
 *
 * Delivered days arrive in halves so a total is exact; an average over three or six months is
 * not, and rounding it is the whole of the display. Kept out of the arithmetic above so that a
 * half shown as 7.7 is still the sum of two quarters shown as 10.0 and 5.3.
 */
export const formatDays = (days: number): string =>
  Number.isInteger(days) ? String(days) : days.toFixed(1);

// --- Export ----------------------------------------------------------------------------------

/**
 * One row per series, carrying every month and every derived figure - the whole of the wide
 * layout, and with it everything the period layout shows, since that table is this row's
 * arithmetic on the combined series.
 *
 * Averages are written rounded, so the file and the screen cannot disagree.
 */
export const utilizationExportRows = (
  series: UtilizationSeries[],
  startYear: number
): Map<string, CellValue>[] => {
  const months = fiscalYearMonths(startYear);

  return series.map(one => {
    const record = new Map<string, CellValue>();
    record.set('Fiscal year', fiscalYearLabel(startYear));
    record.set('Instructor', one.instructorName);

    FISCAL_MONTH_LABELS.forEach((label, index) => {
      record.set(`${label} ${(months[index] ?? '').slice(0, 4)}`, one.days[index] ?? 0);
    });

    FISCAL_PERIOD_ROWS.forEach(period => {
      const { total, average } = periodFigures(one.days, period);
      record.set(`${period.label} total`, total);
      record.set(`${period.label} average`, Number(average.toFixed(1)));
    });

    return record;
  });
};
